import type { FetchArgs } from "@reduxjs/toolkit/query";

/**
 * Bounded fetch timeout for idempotent GET/HEAD requests, so a request the
 * browser would otherwise let pend for minutes (or forever) aborts into
 * RTK's TIMEOUT_ERROR instead. This closes the CUSTOMER-UI-3Z perma-null
 * wedge: a /self that never settles keeps OnboardingRouter and
 * CustomerAccountProvider rendering null indefinitely below the veil. With
 * the timeout, the hang becomes TIMEOUT_ERROR → `shouldRetryRequest` already
 * classifies that as transient → up to MAX_BOOT_RETRIES retries → on
 * exhaustion the splash resolves to the boot error page with a retry button
 * (CUSTOMER-UI-6H path) instead of an indefinite stuck veil.
 *
 * Sizing: ABOVE the BFF's worst-case *successful* end-to-end GET budget —
 * per-upstream `UPSTREAM_TIMEOUT_MS` (10s default, up to 30s for slow
 * services) plus the BFF's own retry layer and SWR fast paths — with margin,
 * so it can never abort a slow-but-succeeding request and amplify the
 * web×BFF retry product (4 web attempts × 4 BFF attempts). The trade-off: a
 * true hang costs up to ~4×45s of retries before the error page. This
 * constant is the tuning knob.
 *
 * Mutations NEVER receive the injected timeout — an aborted in-flight POST
 * could have applied upstream (double-pay risk); bounding mutations stays
 * owned by the BFF's upstream timeouts. Timeouts also fire benignly when a
 * backgrounded tab throttles a fetch while the timeout's setTimeout still
 * runs — `sentryErrorCapture` folds TIMEOUT_ERROR into the single
 * infra-unavailable fingerprint so that noise can't mint per-endpoint issues.
 */
export const BOOT_FETCH_TIMEOUT_MS = 45_000;

/** RTK accepts `string | FetchArgs`; normalize once so the timeout injection
 * and the 401 replay both operate on the same object. */
export function normalizeFetchArgs(args: string | FetchArgs): FetchArgs {
  return typeof args === "string" ? { url: args } : args;
}

export function withBootFetchTimeout(
  args: FetchArgs,
  {
    isExternal,
    isTestEnv,
  }: {
    /** Requests marked external via extraOptions (third-party hosts). */
    isExternal: boolean;
    /** E2E builds: page.clock specs must not race a real 45s timer. */
    isTestEnv: boolean;
  }
): FetchArgs {
  const method = (args.method ?? "GET").toUpperCase();
  const isIdempotent = method === "GET" || method === "HEAD";
  if (
    isTestEnv ||
    isExternal ||
    !isIdempotent ||
    // An explicit caller-set timeout always wins.
    args.timeout !== undefined
  ) {
    return args;
  }
  return { ...args, timeout: BOOT_FETCH_TIMEOUT_MS };
}
